/**
 * EL RECORRIDO DEL ARMADOR POR LOS 10 CAPITULOS DE MADERINCO (MADERINCO-LISTA-ITEMS-SECCIONES-T1, Ares #33756).
 *
 * La lista del cliente (lineasDelCliente.js, generada de la tabla de A) se une con la plantilla que sirve la API
 * (normalizarPlantilla) por el id libro:hoja:celda de cada item (fuenteId). No crea catalogo ni recarga nada: si un
 * id de la lista no esta en la plantilla servida, la linea lo dice; no se inventa el item.
 *
 * Reglas del recorrido (propuesta de A, #33730 seccion 4):
 *  - un paso por capitulo, en el orden del cliente;
 *  - obligatorias marcadas con su item; opcionales se ofrecen para agregar;
 *  - las lineas con opciones piden la opcion;
 *  - lo que va dentro de otro item no se pregunta aparte (se muestra como parte del item);
 *  - lo por confirmar va en gris, sin item y sin sumar;
 *  - las secciones del catalogo que no toca ninguna linea quedan como adicionales al final.
 *
 * Sin DOM: lo prueba scripts/check-capitulos-cliente.mjs.
 *
 * @module application/presupuestos/capitulosDelCliente
 */

import { LINEAS_DEL_CLIENTE } from './lineasDelCliente.js';

export const NUMEROS_DE_CAPITULO = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

// Los nombres como los escribe el cliente (Lista ITEMS presupuesto de obras.xlsx, huella c4142d84c8d7), leidos por
// Ares (#33782). Van tal cual los transcribio Ares; las tildes del Excel quedan por cotejar con el archivo.
export const NOMBRES_DE_CAPITULO = {
  1: 'Estructura de piso', 2: 'Estructura de paredes', 3: 'Estructura de cubierta', 4: 'Salidas hidrosanitarias y de gas',
  5: 'Salidas electricas', 6: 'Carpinteria en madera', 7: 'Carpinteria en aluminio', 8: 'Enchapes', 9: 'Acabados', 10: 'Finales de obra'
};

// MAD-IMPLEMENTAR-DISENO-T1 grupo 3: el nombre de cada paso en la barra de secciones (el de las esenciales donde lo hay).
export const NOMBRES_CORTOS_DE_CAPITULO = {
  1: 'Piso', 2: 'Paredes', 3: 'Cubierta', 4: 'Agua y gas', 5: 'Electricidad', 6: 'Madera', 7: 'Aluminio', 8: 'Enchapes',
  9: 'Acabados', 10: 'Finales'
};

/** El corto solo si la seccion trae el nombre de la lista del cliente; con otro nombre (otra lista) va el suyo entero. */
export function nombreCortoDeCapitulo(c) {
  const corto = NOMBRES_CORTOS_DE_CAPITULO[c?.numero];
  return corto && c.nombre === NOMBRES_DE_CAPITULO[c.numero] ? corto : String(c?.nombre || c?.numero || '');
}

/**
 * La lista del cliente que sirve la API (GET plantilla/capitulos, CapitulosDto de nexo-api 640) en la forma de
 * LINEAS_DEL_CLIENTE, para el mismo recorrido. null = la API no la tiene (fallo, o lista sin cargar): el panel usa su
 * archivo. Acepta camelCase y PascalCase. Lo prueba scripts/check-capitulos-desde-api.mjs.
 */
export function lineasDesdeApi(dto) {
  const capitulos = dto?.capitulos ?? dto?.Capitulos;
  if (!Array.isArray(capitulos) || capitulos.length === 0) return null;
  const v = (o, k) => o?.[k] ?? o?.[k[0].toUpperCase() + k.slice(1)];
  const lineas = [...capitulos]
    .sort((a, b) => (v(a, 'orden') ?? v(a, 'capitulo')) - (v(b, 'orden') ?? v(b, 'capitulo')))
    .flatMap((c) => [...(v(c, 'lineas') || [])]
      .sort((a, b) => (v(a, 'orden') ?? 0) - (v(b, 'orden') ?? 0))
      .map((l) => ({
        capitulo: Number(v(c, 'capitulo')),
        linea: String(v(l, 'linea') ?? ''),
        obligatoria: v(l, 'obligatoria') === true,
        opciones: Array.isArray(v(l, 'opciones')) ? v(l, 'opciones') : [],
        seccionCatalogo: v(l, 'seccionCatalogo') ?? null,
        fuenteIds: Array.isArray(v(l, 'fuenteIds')) ? v(l, 'fuenteIds') : [],
        porConfirmar: v(l, 'porConfirmar') === true,
        dentroDeOtroItem: v(l, 'dentroDeOtroItem') === true,
        notaPorConfirmar: v(l, 'nota') ?? null,
        // Con mas de un item que casa, cual manda queda para Maderinco (condicion 3 de Ares #36348). El archivo no lo trae.
        cualMandaPorConfirmar: v(l, 'cualMandaPorConfirmar') === true
      })));
  return lineas.length ? lineas : null;
}

/** El libro de un item para mostrar al lado del equivalente: libro · hoja, o lo que diga su fuenteId. */
export function libroDelItem(it) {
  if (it?.libro) return [it.libro, it.hoja].filter(Boolean).join(' · ');
  const p = String(it?.fuenteId || '').split(':');
  return p.length >= 3 ? `${p[0]} · ${p[1]}` : '';
}

/** El estado de una linea frente a la plantilla servida. */
export function estadoDeLinea(linea, items) {
  if (linea.porConfirmar) return 'por_confirmar';
  if (linea.dentroDeOtroItem) return 'dentro_de_otro';
  if (items.length) return 'con_item';
  return 'sin_item_en_plantilla';
}

/** Cuenta para el total: solo lo que tiene item y no esta por confirmar ni va dentro de otro. */
export const sumaAlTotal = (estado) => estado === 'con_item';

/**
 * Josse 28-oct (#36553): el armador tiene UNA estructura, las secciones del cliente. Cada arbol de Atenea (esencial) va
 * dentro de la seccion del cliente con la que comparte MAS ids de catalogo; empate = la de numero menor; sin ids
 * compartidos no va en ninguna. Medido con el archivo de A: piso 1, paredes 2, cubierta 3, agua y gas 4, electricidad 5.
 * @returns {Map<number, string[]>} numero de seccion -> ids de esencial, en el orden de las esenciales
 */
export function esencialesPorSeccion(lineas, esenciales) {
  const m = new Map();
  for (const e of esenciales || []) {
    const ids = new Set(e.fuenteIds || []);
    const cuenta = new Map();
    for (const l of lineas || []) for (const f of l.fuenteIds || []) if (ids.has(f)) cuenta.set(l.capitulo, (cuenta.get(l.capitulo) || 0) + 1);
    let mejor = null;
    for (const [c, k] of cuenta) if (mejor === null || k > cuenta.get(mejor) || (k === cuenta.get(mejor) && c < mejor)) mejor = c;
    if (mejor !== null) m.set(mejor, [...(m.get(mejor) || []), e.id]);
  }
  return m;
}

/** La seccion del cliente de cada item servido (por la linea que lo usa; la primera gana). Map item.id -> numero. */
export function seccionDeCadaItem(recorrido) {
  const m = new Map();
  for (const c of recorrido?.capitulos || []) for (const l of c.lineas) for (const it of l.items) if (!m.has(it.id)) m.set(it.id, c.numero);
  return m;
}

/**
 * "Guiame seccion por seccion" (#36553 punto 4): recorre las secciones del cliente en su orden. Cada paso dice que
 * obligatorias faltan en el presupuesto; nada se agrega solo y al final NO se declara el presupuesto completo.
 * @param {{recorrido: object, yaPuestos?: Set<string>, listas?: Set<number>}} o listas = secciones que ya se dieron por vistas
 */
export function pasoPorSecciones({ recorrido, yaPuestos = new Set(), listas = new Set() }) {
  const caps = recorrido?.capitulos || [];
  const i = caps.findIndex((c) => !listas.has(c.numero));
  if (i < 0) {
    return { numero: null, paso: 'fin', opciones: [], texto: `Ya pasamos por las ${caps.length} secciones.`,
      nota: 'No doy el presupuesto por completo: revísalo tú antes de guardarlo.' };
  }
  const c = caps[i];
  const faltan = c.lineas.filter((l) => l.obligatoria && l.estado === 'con_item' && !l.items.some((it) => yaPuestos.has(it.id)));
  const texto = `Sección ${c.numero}: ${c.nombre}. ` + (faltan.length
    ? `Falta${faltan.length === 1 ? '' : 'n'} ${faltan.length} obligatoria${faltan.length === 1 ? '' : 's'}: ${faltan.slice(0, 3).map((l) => l.linea).join(', ')}${faltan.length > 3 ? '…' : ''}.`
    : 'Sus obligatorias ya están en tu presupuesto.');
  const sig = caps[i + 1];
  return { numero: c.numero, nombre: c.nombre, paso: 'seccion', texto,
    nota: faltan.length ? 'Escribe la cantidad y toca Agregar en cada una: no agrego nada solo.' : '',
    opciones: [
      { label: `Abrir ${c.nombre}`, accion: { tipo: 'abrir-seccion', numero: c.numero } },
      { label: sig ? `Listo, sigue ${sig.nombre}` : 'Listo', accion: { tipo: 'listo-seccion', numero: c.numero } }
    ] };
}

/**
 * @param {{secciones: Array<{id, nombre, orden, items: Array<{id, fuenteId}>}>}} plantilla la de normalizarPlantilla
 * @param {Array} [lineas] LINEAS_DEL_CLIENTE por defecto
 */
export function recorridoPorCapitulos(plantilla, lineas = LINEAS_DEL_CLIENTE) {
  const secciones = plantilla?.secciones || [];
  const porFuente = new Map();
  for (const s of secciones) for (const it of s.items || []) {
    if (it.fuenteId && !porFuente.has(it.fuenteId)) porFuente.set(it.fuenteId, { ...it, seccionId: s.id, seccionClave: s.clave, seccionNombre: s.nombre });
  }
  const usados = new Set();
  const capitulos = NUMEROS_DE_CAPITULO.map((numero) => ({
    numero,
    nombre: NOMBRES_DE_CAPITULO[numero],
    lineas: lineas.filter((l) => l.capitulo === numero).map((l) => {
      const items = l.fuenteIds.map((id) => porFuente.get(id)).filter(Boolean);
      const faltan = l.fuenteIds.filter((id) => !porFuente.has(id));
      for (const it of items) usados.add(it.seccionId);
      return { ...l, items, faltan, estado: estadoDeLinea(l, items) };
    })
  }));
  // Las secciones servidas que no toca ninguna linea del cliente: adicionales al final (chimenea, hall, sotano...).
  const adicionales = secciones.filter((s) => (s.items || []).length && !usados.has(s.id));
  const todas = capitulos.flatMap((c) => c.lineas);
  return {
    capitulos,
    adicionales,
    resumen: {
      lineas: todas.length,
      conItem: todas.filter((l) => l.estado === 'con_item').length,
      dentroDeOtro: todas.filter((l) => l.estado === 'dentro_de_otro').length,
      porConfirmar: todas.filter((l) => l.estado === 'por_confirmar').length,
      sinItemEnPlantilla: todas.filter((l) => l.estado === 'sin_item_en_plantilla').length,
      idsQueNoSirveLaPlantilla: [...new Set(todas.flatMap((l) => l.faltan))]
    }
  };
}
